import {
  InsiderAppUsageChoice,
  InsiderPokerFormatChoice,
  InsiderSolverConfidenceChoice,
  InsiderStakesChoice,
  InsiderTrackChoice,
  type ListInsiderMembersParams,
  SubscriptionTier,
} from '@gto/api-client/admin/react-query'

/**
 * The cohort model: what a filter set is and what it asks the server for.
 *
 * Separate from `members-search.ts`, which owns the URL contract, because the
 * panel, the table's adaptive reveal, the empty state and the export all have
 * to agree on what "filtering on stakes" means — and they can only do that by
 * reading one definition rather than five.
 *
 * **OR inside a facet, AND across facets.** Ticking PLO cash and PLO MTT means
 * "plays either", because that is how a person reads two ticked boxes in one
 * group; adding High stakes narrows to "either of those formats AND high
 * stakes". AND-inside-a-facet is a real question, but a rarer one, and no facet
 * UI communicates it without saying so — which is why the panel says this out
 * loud rather than leaving it to be inferred from a surprising row count.
 */

/**
 * Every facet's values come from the generated enums rather than a hand-written
 * list. A choice added on the backend becomes filterable the moment the client
 * is regenerated; re-listing them here would type-check forever and leave the
 * new cohort reachable only by hand-edited URL.
 */
const enumValues = <TChoice extends string>(choices: Record<string, TChoice>) =>
  Object.values(choices) as [TChoice, ...TChoice[]]

export const POKER_FORMAT_VALUES = enumValues(InsiderPokerFormatChoice)
export const STAKES_VALUES = enumValues(InsiderStakesChoice)
export const SOLVER_CONFIDENCE_VALUES = enumValues(InsiderSolverConfidenceChoice)
export const TRACK_VALUES = enumValues(InsiderTrackChoice)
export const APP_USAGE_VALUES = enumValues(InsiderAppUsageChoice)
export const SUBSCRIPTION_TIER_VALUES = enumValues(SubscriptionTier)

/**
 * Buckets rather than a slider, and provisional ones. `years_with_tools` is a
 * raw integer; C-1911 — which would measure how it actually clusters — is
 * blocked on the GTO Brain grants in C-1981, so these are a shape chosen to be
 * obviously provisional rather than a measured one.
 *
 * `any` is the absence of the facet, not a fourth bucket, which is why it never
 * reaches the wire.
 */
export const YEARS_BUCKETS = ['0-2', '3-5', '6+'] as const
export type YearsBucket = (typeof YEARS_BUCKETS)[number]

export const YEARS_BUCKET_LABEL: Record<YearsBucket, string> = {
  '0-2': '0–2 years',
  '3-5': '3–5 years',
  '6+': '6+ years',
}

/**
 * Each bucket's bounds, stated ONCE, so C-1911 moving a boundary is one edit
 * and cannot leave the panel's label and the query disagreeing about the same
 * bucket. `max: null` is an open upper end, not a missing value.
 */
export const YEARS_BUCKET_BOUNDS: Record<YearsBucket, { min: number; max: number | null }> = {
  '0-2': { min: 0, max: 2 },
  '3-5': { min: 3, max: 5 },
  '6+': { min: 6, max: null },
}

/**
 * The two never-asked sentinels, each deliberately spelled as the query
 * parameter it becomes.
 *
 * Neither travels inside its facet's array: `app_usage` is a
 * `list[InsiderAppUsageChoice]` and `subscription_tier` a `list[SubscriptionTier]`
 * on the server, so a token outside the enum 422s the request rather than
 * filtering. Each gets a boolean parameter of its own and the enum parameters
 * stay honestly typed.
 *
 * Both are first-class options rather than absences because without them the
 * only way to ask "who has none of these" is to tick every real value and read
 * the gap — which is not something a reader can be expected to invent.
 */
export const APP_USAGE_MISSING = 'app_usage_missing'
export const NO_SUBSCRIPTION = 'no_subscription'

export type AppUsageFilterValue = InsiderAppUsageChoice | typeof APP_USAGE_MISSING
export type SubscriptionFilterValue = SubscriptionTier | typeof NO_SUBSCRIPTION

export type MemberFilters = {
  poker_formats: InsiderPokerFormatChoice[]
  stakes: InsiderStakesChoice[]
  solver_confidence: InsiderSolverConfidenceChoice[]
  years: YearsBucket | undefined
  tracks: InsiderTrackChoice[]
  app_usage: AppUsageFilterValue[]
  subscription: SubscriptionFilterValue[]
}

export const EMPTY_FILTERS: MemberFilters = {
  poker_formats: [],
  stakes: [],
  solver_confidence: [],
  years: undefined,
  tracks: [],
  app_usage: [],
  subscription: [],
}

/**
 * Which facets the operator is currently filtering on. This is what makes the
 * table adaptive: a facet in use reveals its own column, so a cohort can be
 * checked against the query that produced it without hunting through the
 * Columns menu — and a plain lookup, which filters on nothing, pays nothing.
 */
export type FilterKey = keyof MemberFilters

export const activeFilterKeys = (filters: MemberFilters): FilterKey[] =>
  (Object.keys(filters) as FilterKey[]).filter((key) => {
    const value = filters[key]

    return Array.isArray(value) ? value.length > 0 : value !== undefined
  })

export const activeFilterCount = (filters: MemberFilters): number =>
  activeFilterKeys(filters).length

/**
 * Whether the app-usage facet is narrowing WITHOUT the never-asked option — the
 * one combination that drops rows by construction rather than by answer, and so
 * the one the third empty state has to name.
 *
 * `app_usage` is empty for every signup predating the field, so filtering on any
 * real value silently excludes that whole population. A plain "no results" lets
 * a reader conclude the data is empty when it is really unasked.
 */
export const isFilteringOutNeverAsked = (filters: MemberFilters): boolean =>
  filters.app_usage.length > 0 && !filters.app_usage.includes(APP_USAGE_MISSING)

/**
 * A facet's real enum members, with its sentinel dropped — the shape the
 * endpoint's `list[...]` parameter accepts. Written as two explicit predicates
 * rather than one generic helper because a generic cannot narrow a union array
 * back to the non-sentinel arm, and the version that compiled did so by
 * widening both facets to `string[]`, which is exactly the honesty this file
 * exists to keep.
 */
export const appUsageValues = (selected: AppUsageFilterValue[]): InsiderAppUsageChoice[] =>
  selected.filter((value): value is InsiderAppUsageChoice => value !== APP_USAGE_MISSING)

export const subscriptionTierValues = (selected: SubscriptionFilterValue[]): SubscriptionTier[] =>
  selected.filter((value): value is SubscriptionTier => value !== NO_SUBSCRIPTION)

/**
 * The filter set as the list endpoint's parameters.
 *
 * An empty facet is the ABSENCE of its key rather than an empty array: axios
 * serializes `[]` to nothing anyway, but omitting it keeps the request the
 * network tab shows honest about what is actually being asked.
 *
 * Shared by the list query and the export so the CSV cannot describe a
 * different cohort from the grid that produced it — two builders is two places
 * they can drift.
 */
export const memberFiltersToParams = (
  filters: MemberFilters,
): Partial<ListInsiderMembersParams> => {
  const appUsage = appUsageValues(filters.app_usage)
  const tiers = subscriptionTierValues(filters.subscription)
  const years = filters.years === undefined ? undefined : YEARS_BUCKET_BOUNDS[filters.years]

  return {
    ...(filters.poker_formats.length > 0 && { poker_formats: filters.poker_formats }),
    ...(filters.stakes.length > 0 && { stakes: filters.stakes }),
    ...(filters.solver_confidence.length > 0 && {
      solver_confidence: filters.solver_confidence,
    }),
    ...(years !== undefined && { years_min: years.min }),
    // An open upper end omits the key rather than sending null — `6+` means "no
    // ceiling", and a ceiling of null is a bound the endpoint would have to
    // interpret rather than one it simply never receives.
    ...(years?.max != null && { years_max: years.max }),
    ...(filters.tracks.length > 0 && { tracks: filters.tracks }),
    ...(appUsage.length > 0 && { app_usage: appUsage }),
    ...(filters.app_usage.includes(APP_USAGE_MISSING) && { app_usage_missing: true }),
    ...(tiers.length > 0 && { subscription_tier: tiers }),
    ...(filters.subscription.includes(NO_SUBSCRIPTION) && { no_subscription: true }),
  }
}
